import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, TextField, InputAdornment, CircularProgress,
    List, ListItemButton, ListItemText, Chip, Alert,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Inventory2 as Inventory2Icon } from '@mui/icons-material';
import { listParts } from './partsApi';
import { apiErrorMessage } from '../../api/apiError';

// ---------------------------------------------------------------------------
// PartsList — current-rev default, minimal search/filter, "New part" entry
// point (doc §5 IA: parts list -> part detail -> feature detail).
// ---------------------------------------------------------------------------

function PartsList({ onNewPart, onSelectPart, refreshToken }) {
    const [search, setSearch] = useState('');
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchParts = useCallback(async (searchTerm) => {
        setLoading(true);
        setError(null);
        try {
            const { ok, status, data } = await listParts({ search: searchTerm });
            if (!ok) {
                setError(apiErrorMessage(status, data, 'Could not load parts.'));
                setParts([]);
                return;
            }
            setParts(Array.isArray(data.parts) ? data.parts : []);
        } catch (e) {
            setError('Network error loading parts.');
            setParts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const handle = setTimeout(() => fetchParts(search), search ? 300 : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, fetchParts, refreshToken]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search part number or name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        minWidth: 260,
                        '& .MuiOutlinedInput-root': { color: 'rgba(255,255,255,0.9)' },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={onNewPart}
                    sx={{ ml: 'auto' }}
                >
                    New Part
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}

            <Paper sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.3)' }} />
                    </Box>
                ) : parts.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6, opacity: 0.6 }}>
                        <Inventory2Icon sx={{ fontSize: 40, color: 'rgba(144,202,249,0.4)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {search ? 'No parts match your search.' : 'No parts yet — create your first part to get started.'}
                        </Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {parts.map((part) => (
                            <ListItemButton
                                key={part.id}
                                onClick={() => onSelectPart(part)}
                                sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)', py: 1.25 }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography component="span" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                                                {part.part_number}
                                            </Typography>
                                            {part.revision && (
                                                <Chip label={`Rev ${part.revision}`} size="small" sx={{ height: 18, fontSize: '0.65rem', backgroundColor: 'rgba(144,202,249,0.12)', color: '#90caf9' }} />
                                            )}
                                        </Box>
                                    }
                                    secondary={
                                        <Typography component="span" variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                                            {part.name || '—'}
                                            {typeof part.feature_count === 'number' ? ` · ${part.feature_count} feature${part.feature_count === 1 ? '' : 's'}` : ''}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Paper>
        </Box>
    );
}

export default PartsList;
